import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { FaEnvelope, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { authAPI, translateBackendMessage } from "../../api/apiSystem";
import "./LoginForm.css";

const FormField = ({
  controlId,
  label,
  type,
  placeholder,
  value,
  onChange,
  icon: Icon,
}) => (
  <Form.Group className="mb-3" controlId={controlId}>
    <Form.Label>{label}</Form.Label>
    <div className="position-relative">
      <Icon className="form-icon" />
      <Form.Control
        type={type}
        placeholder={placeholder}
        className="ps-5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
      />
    </div>
  </Form.Group>
);

export default function VehicleLoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const showAlert = (icon, title, text) => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText: "OK",
      confirmButtonColor: "#fd7e14",
      background: "#f8f9fa",
      color: "#333",
    });
  };
  
  const getNavigationPath = (userType) => {
    const paths = {
      driver_company: "/driver-company/active-orders",
      vehicle: "/vehicle/active-orders",
    };
    return paths[userType] || "/";
  };

  const handleLoginSuccess = (response) => {
    const {
      token,
      driver,
      vehicle,
      user_type,
    } = response.data.data;

    // المستخدم الفعلي
    const loggedUser = driver || vehicle;

    if (!token || !loggedUser || !user_type) {
      console.error("Invalid login response:", response.data);

      Swal.fire({
        icon: "error",
        title: "Login Error",
        text: "Invalid login response from server.",
      });

      return;
    }

    // تخزين البيانات
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(loggedUser));
    localStorage.setItem("userType", user_type);

    window.dispatchEvent(new Event("userTypeChanged"));

    Swal.fire({
      icon: "success",
      title: t("loginForm.alerts.success.title") || "Success",
      text:
        user_type === "vehicle"
          ? "Vehicle login successful"
          : "Driver Company login successful",
      timer: 2000,
      showConfirmButton: false,
    });

    setTimeout(() => {
      navigate(getNavigationPath(user_type));
    }, 2000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.vehicleLogin(formData);

      if (response.success && (response.data?.data?.token || response.data?.data?.access_token)) {
        handleLoginSuccess(response);
      } else {
        const errorMessage = response.message || t("loginForm.error.invalidCredentials");
        showAlert("error", t("loginForm.alerts.error.title"), errorMessage);
      }
    } catch (error) {
      const errorMessage = translateBackendMessage(
        error?.response?.data || error,
        t("loginForm.error.networkError"),
      );
      showAlert("error", t("loginForm.alerts.error.title"), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="login-section">
      <Container>
        <Row className="justify-content-center align-items-center h-100">
          <Col md={6} lg={5}>
            <Card className="shadow-lg p-4 border-0 login-card">
              <Card.Body>
                <h2 className="text-center mb-4 text-dark fw-bold">
                  {t("loginForm.subDriverLogin") || "تسجيل دخول سائق فرعي"}
                </h2>
                <p className="text-center text-muted mb-4">
                  {t("loginForm.subDriverDescription") || "الرجاء إدخال بيانات المركبة الخاصة بك"}
                </p>

                <Form onSubmit={handleLogin}>
                  <FormField
                    controlId="formEmail"
                    label={t("loginForm.emailLabel")}
                    type="email"
                    placeholder={t("loginForm.emailPlaceholder")}
                    value={formData.email}
                    onChange={(value) => handleInputChange("email", value)}
                    icon={FaEnvelope}
                  />

                  <Form.Group className="mb-3" controlId="formPassword">
                    <Form.Label>{t("loginForm.passwordLabel")}</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder={t("loginForm.passwordPlaceholder")}
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                      />
                      <span
                        className="password-toggle-icon"
                        onClick={() => setShowPassword(prev => !prev)}
                        style={{ cursor: "pointer", position: "absolute", right: "15px", top: "50%", transform: "translateY(-50%)" }}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                  </Form.Group>

                  <Button
                    variant="warning"
                    type="submit"
                    className="w-100 bg-orange-500 text-white mb-3"
                    disabled={loading}
                  >
                    {loading ? t("loginForm.submitButtonLoading") : t("navbar.login")}
                  </Button>
                </Form>

                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={() => navigate("/login")}
                >
                  {t("loginForm.mainDriverLogin") || "العودة لتسجيل الدخول الرئيسي"}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
